/**
 * Returns a function, that, when invoked, will only be triggered at most once during
 * a given window of time.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Time window (in milliseconds).
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.whenDefined('product-form').then(() => {
    class StickyAtc extends customElements.get('product-form') {
      constructor() {
        super();

        this.section = this.closest('.section-main-product');
        this.buyButtons = this.section.querySelector('.quantity-submit-row');
        if (!this.buyButtons) return;

        this.mainProductForm = this.section.querySelector('product-form');
        this.mainProductSubmitForm = this.mainProductForm.getSubmitForm();
        this.submitForm = this.getSubmitForm();
        this.imageContainer = this.querySelector('.sticky-atc__image');
        this.variantTitle = this.querySelector('.sticky-atc__details__variant__title');
        this.variantTitle?.toggleAttribute('hidden', !this.section.querySelector('variant-picker'));
        this.button = this.querySelector('.add-to-cart');

        this.setupSizeSelectionRequirement();
        this.setupCustomerService();

        this.throttledOnScroll = throttle(this.handleScroll.bind(this));
        window.addEventListener('scroll', this.throttledOnScroll);

        this.section.addEventListener('on:media-gallery:change', this.updateImage.bind(this));

        this.updateImage();
        this.updateAddToCartButton();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.throttledOnScroll);
      }

      /**
       * Handles submission of the product form.
       * @param {object} evt - Event object.
       */
      async handleSubmit(evt) {
        evt.preventDefault();

        if (!this.hasExplicitSizeSelection()) {
          this.revealSizeSelector();
          return;
        }

        const variantId = this.section.querySelector('.js-product-form input[name="id"]').value;
        if (!variantId) {
          const target = this.section.querySelector('#product-info');
          if (target && target.offsetParent) {
            evt.preventDefault();
            theme.scrollToRevealElement(target);
          }
          return;
        }

        // Validate main form
        const customFormValid = this.mainProductForm.validate();
        if (!customFormValid || !this.mainProductSubmitForm.reportValidity()) {
          evt.preventDefault();
          if (!customFormValid) {
            theme.scrollToRevealElement(this.mainProductForm);
          } else {
            const input = Array.from(this.mainProductSubmitForm.elements).find(
              (el) => !el.checkValidity()
            );
            setTimeout(() => theme.scrollToRevealElement(input), 100);
          }
          return;
        }

        // Clear copied data
        this.submitForm.querySelectorAll('[data-copied]').forEach((el) => el.remove());

        // Copy data
        const formData = new FormData(this.mainProductSubmitForm);
        for (const p of formData) {
          if (!this.submitForm.querySelector(`[name="${p[0]}"]`)) {
            const input = document.createElement('input');
            input.name = p[0];
            input.value = p[1];
            input.hidden = true;
            input.setAttribute('data-copied', '');
            this.submitForm.append(input);
          }
        }
        super.handleSubmit(evt);
      }

      setupSizeSelectionRequirement() {
        if (this.dataset.requireSizeSelection !== 'true') return;

        if (!this.section.dataset.yeeseSizeChosen) {
          this.section.dataset.yeeseSizeChosen = new URLSearchParams(window.location.search).has('variant')
            ? 'true'
            : 'false';
        }

        if (this.section.dataset.yeeseSizeListener === 'true') return;
        this.section.dataset.yeeseSizeListener = 'true';
        this.section.addEventListener('change', (event) => {
          const selector = event.target.closest?.('variant-picker .option-selector');
          if (!selector || !this.isSizeSelector(selector)) return;
          this.section.dataset.yeeseSizeChosen = 'true';
          selector.classList.remove('yeese-size-required');
          selector.querySelector('.yeese-size-required__message')?.remove();
        });
      }

      isSizeSelector(selector) {
        const optionName = (selector?.dataset.option || '').toLowerCase();
        return /size|größe|grösse|taille|talla|taglia|maat|tamanho|サイズ/.test(optionName);
      }

      getSizeSelector() {
        return Array.from(this.section.querySelectorAll('variant-picker .option-selector'))
          .find((selector) => this.isSizeSelector(selector));
      }

      hasExplicitSizeSelection() {
        if (this.dataset.requireSizeSelection !== 'true') return true;
        const sizeSelector = this.getSizeSelector();
        if (!sizeSelector) return true;
        return this.section.dataset.yeeseSizeChosen === 'true';
      }

      revealSizeSelector() {
        const selector = this.getSizeSelector();
        if (!selector) return;

        selector.classList.add('yeese-size-required');
        let message = selector.querySelector('.yeese-size-required__message');
        if (!message) {
          message = document.createElement('div');
          message.className = 'yeese-size-required__message';
          message.setAttribute('role', 'alert');
          message.textContent = this.dataset.sizeRequiredMessage || 'Please choose a size.';
          const buttons = selector.querySelector('.option-selector__btns, custom-select');
          if (buttons) selector.insertBefore(message, buttons);
          else selector.append(message);
        }

        theme.scrollToRevealElement(selector);
        setTimeout(() => {
          selector.querySelector('input:not([disabled]), button, [tabindex="0"]')?.focus({ preventScroll: true });
        }, 350);
      }

      setupCustomerService() {
        const serviceLink = this.querySelector('[data-sticky-customer-service]');
        if (!serviceLink) return;

        const hideNativeLauncher = () => {
          if (window.matchMedia('(max-width: 767.98px)').matches) {
            window.tidioChatApi?.display?.(false);
          }
        };

        const prepareTidio = () => {
          hideNativeLauncher();
          window.tidioChatApi?.on?.('close', hideNativeLauncher);
        };

        if (window.tidioChatApi) prepareTidio();
        else document.addEventListener('tidioChat-ready', prepareTidio, { once: true });

        serviceLink.addEventListener('click', (event) => {
          if (window.tidioChatApi?.open) {
            event.preventDefault();
            window.tidioChatApi.display?.(true);
            window.tidioChatApi.open();
            return;
          }

          event.preventDefault();
          let opened = false;
          const openChat = () => {
            if (!window.tidioChatApi?.open) return;
            opened = true;
            window.tidioChatApi.display?.(true);
            window.tidioChatApi.open();
          };
          document.addEventListener('tidioChat-ready', openChat, { once: true });
          window.setTimeout(() => {
            if (!opened) window.location.href = serviceLink.href;
          }, 2500);
        });
      }

      /**
       * Determine visibility after scroll.
       */
      handleScroll() {
        const topOffset = document.querySelector('.pageheader--sticky') ? document.querySelector('.section-header').offsetHeight : 0;
        this.classList.toggle(
          'sticky-atc--out',
          this.buyButtons.getBoundingClientRect().bottom > topOffset
        );

        document.body.classList.toggle(
          'scrolled-to-bottom',
          window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
        );
      }

      /**
       * Updates the image.
       */
      updateImage() {
        const mainImage = this.section.querySelector('.media-gallery .main-image .slider__item.is-active .product-media img');

        if (mainImage) {
          this.imageContainer.innerHTML = mainImage.outerHTML;
        } else {
          this.imageContainer.textContent = '';
        }
      }

      /**
       * Updates the add to cart button text.
       */
      updateAddToCartButton() {
        // If there is no variant selected, show unavailable-text
        const variantId = this.section.querySelector('.js-product-form input[name="id"]').value;
        if (!variantId) {
          this.button.innerHTML = this.button.dataset.unavailableText;
        }
      }
    }

    customElements.define('sticky-atc', StickyAtc);
  });
}
